import React, { useState } from 'react';
import { PrepRequestItem } from 'models/prep-request.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import RequestStatusTag from '../request-status-tag';
import { ArrowDownIcon } from 'cokitchen-icons';
import { useLocation } from 'react-router-dom';

interface RequestItemWithMenu extends PrepRequestItem {
  actionsMenu?: React.ReactNode;
}

type RequestItemsListProps = {
  items: RequestItemWithMenu[];
  loading?: boolean;
  requestStatus?: ENUM_REQUEST_STATUS;
  showDetailsToggle?: boolean;
  actionsMenu?: React.ReactNode;
};

const RequestItemsList: React.FC<RequestItemsListProps> = ({
  items,
  requestStatus,
  loading = false,
  showDetailsToggle = false
}) => {
  const { pathname } = useLocation();
  const isKitchenRoute = pathname.includes('kitchen');
  const [showDetails, setShowDetails] = useState<Record<number, boolean>>({});

  const toggleDetails = (index: number) => {
    setShowDetails((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-8'>
        <p className=''>Loading items...</p>
      </div>
    );
  }

  return (
    <div className='px-2 md:px-2'>
      {items?.map((item, index) => (
        <div key={item.id}>
          <div className='flex flex-col sm:flex-row sm:justify-between border-b border-b-[#EAEBEB] bg-[#F7F7F7E5] px-3 md:px-4 py-4 md:py-4 gap-3 sm:gap-6 items-start sm:items-center'>
            <div className='flex-1'>
              <p className='font-medium text-[#2E3031] flex items-center capitalize text-sm md:text-base'>
                {item.ingredient?.name || 'Unknown Item'}
                {(requestStatus === ENUM_REQUEST_STATUS.SENT && item.quantity_sent) ||
                requestStatus === ENUM_REQUEST_STATUS.RECEIVED ? (
                  <span className='ml-4'>
                    <RequestStatusTag status={item.status} />
                  </span>
                ) : null}
              </p>
              {requestStatus === ENUM_REQUEST_STATUS.SENT ||
              requestStatus === ENUM_REQUEST_STATUS.RECEIVED ? (
                <div className='flex gap-2 text-sm items-center '>
                  <h4 className='font-medium text-[#2E3031] capitalize text-sm '>
                    {item.quantity_requested} items requested
                  </h4>
                  <span className='w-1 h-1 rounded-full bg-[#D9D9D9]'></span>

                  <h4 className='font-medium text-blue-5  text-sm '>
                    {item.quantity_sent ?? 'No'} items sent
                  </h4>
                </div>
              ) : (
                <h3 className='font-medium text-[#6F7376] capitalize text-xs md:text-sm'>
                  {item.quantity_requested} {item.item_type || item.ingredient?.unit_type}
                </h3>
              )}
            </div>
            {(item.prep_item_ingredients && item.prep_item_ingredients.length > 0) ||
            !showDetailsToggle ? (
              <div className='flex gap-2 md:items-center md:gap-3 self-end sm:self-center'>
                {isKitchenRoute &&
                  requestStatus !== ENUM_REQUEST_STATUS.CANCELLED &&
                  item.actionsMenu}
                {showDetailsToggle &&
                  item.prep_item_ingredients &&
                  item.prep_item_ingredients.length > 0 && (
                    <div
                      className={`${
                        showDetails[index] ? 'rotate-180' : ''
                      } cursor-pointer bg-gray-1 hover:bg-gray-2  rounded-full border-[#EAEBEB]`}
                      onClick={() => toggleDetails(index)}>
                      <ArrowDownIcon className='w-6 h-6' />
                    </div>
                  )}
              </div>
            ) : null}
          </div>

          {showDetailsToggle && showDetails[index] && item.prep_item_ingredients.length > 0 && (
            <ul className='flex flex-col gap-2 px-3 md:px-2 py-2 bg-white'>
              {item.prep_item_ingredients.map((prepIngredient, itemIndex: number) => (
                <li
                  key={prepIngredient.id}
                  className={`flex flex-wrap items-center gap-2 md:gap-3 py-2 md:py-3 text-xs md:text-sm text-[#2E3031] ${
                    itemIndex < (item.prep_item_ingredients?.length ?? 0) - 1
                      ? 'border-b border-b-[#EAEBEB]'
                      : ''
                  }`}>
                  <span className='font-bold text-[#5E6164]'>
                    {prepIngredient.ingredient?.name || 'Unknown Ingredient'}
                  </span>
                  <span className='w-1 h-1 rounded-full bg-[#D9D9D9]'></span>
                  <span className='text-[#6F7376]'>
                    {prepIngredient.quantity_requested}
                    {' ' + prepIngredient.ingredient?.unit_type || 'units'} requested
                  </span>
                  {prepIngredient.quantity_sent && (
                    <>
                      <span className='w-1 h-1 rounded-full bg-[#D9D9D9]'></span>
                      <span className='text-blue-4'>
                        {prepIngredient.quantity_sent}
                        {prepIngredient.ingredient?.unit_type || 'units'} sent
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

export default RequestItemsList;
